// API Configuration
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

// Derive the server origin (without /api suffix) for resolving file URLs
export const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');

/**
 * Resolve a backend file path (e.g. /uploads/logos/file.png) to a full URL.
 * Returns the input unchanged if it's already absolute (http/https/data URI).
 */
export function resolveFileUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (/^(https?:|data:)/.test(path)) return path;
  return `${API_ORIGIN}${path}`;
}

// Import token storage
import { tokenStorage } from '@/utils/token-storage';
import { logAuthStage } from '@/lib/auth-log';
import { compactQueryParams } from '@/lib/query-params';

// Unauthenticated auth endpoints: a 401 from these is a real, user-facing
// error (bad credentials, invalid/expired reset token) — NOT an expired
// session. They must never trigger the token-refresh / forced-logout path,
// otherwise the genuine error is hidden behind a redirect to /login.
const AUTH_FLOW_PREFIX = '/auth/';

// API Client
export class ApiClient {
    private baseURL: string;
    private isRefreshing = false;
    private failedQueue: Array<{ resolve: Function; reject: Function }> = [];

    constructor(baseURL: string = API_BASE_URL) {
        this.baseURL = baseURL;
    }

    /**
     * Redirect to login after an unrecoverable session expiry, preserving the
     * current location and signalling why so LoginPage can show a notice
     * instead of bouncing the user with no explanation.
     */
    private redirectToLogin(): void {
        if (window.location.pathname.startsWith('/login')) return;
        const from = window.location.pathname + window.location.search;
        const params = new URLSearchParams({ session: 'expired', from });
        window.location.href = `/login?${params.toString()}`;
    }

    private async refreshTokens(): Promise<void> {
        const refreshToken = tokenStorage.getRefreshToken();
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        const response = await fetch(`${this.baseURL}/auth/refresh`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) {
            tokenStorage.clearAll();
            logAuthStage('token-refresh-failed', { status: response.status });
            // Force redirect to login on refresh failure (preserve return URL)
            this.redirectToLogin();
            throw new Error('Token refresh failed');
        }

        const data = await response.json();
        tokenStorage.setTokens(data.access_token, data.refresh_token);

        // Fetch fresh user data with permissions after token refresh
        try {
            const userData = tokenStorage.getUserData();
            if (userData && userData.id) {
                const userResponse = await fetch(`${this.baseURL}/users/${userData.id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${data.access_token}`,
                    },
                });

                if (userResponse.ok) {
                    const freshUserData = await userResponse.json();
                    // Update user data in localStorage with fresh permissions and roles
                    tokenStorage.setUserData(freshUserData);
                }
            }
        } catch (error) {
            // Log error but don't fail the refresh - tokens are already updated
            console.error('Failed to refresh user data after token refresh:', error);
        }
    }

    private async processQueue(error: Error | null, token: string | null = null): Promise<void> {
        this.failedQueue.forEach(({ resolve, reject }) => {
            if (error) {
                reject(error);
            } else {
                resolve(token);
            }
        });

        this.failedQueue = [];
    }

    private async request<T>(
        endpoint: string,
        options: RequestInit = {},
        retriedAfterRefresh = false,
    ): Promise<T> {
        const url = `${this.baseURL}${endpoint}`;

        // Get token from storage
        const token = tokenStorage.getToken();

        // Check if body is FormData to handle file uploads properly
        const isFormData = options.body instanceof FormData;

        const config: RequestInit = {
            headers: {
                // Don't set Content-Type for FormData - let browser handle it
                ...(!isFormData && { 'Content-Type': 'application/json' }),
                // Add Authorization header if token exists
                ...(token && { Authorization: `Bearer ${token}` }),
                ...options.headers,
            },
            ...options,
        };

        try {
            const response = await fetch(url, config);

            // Handle 401 Unauthorized - token might be expired.
            // Skip this entirely for the unauthenticated /auth/* endpoints: a
            // 401 there is a real error (bad credentials, invalid/expired reset
            // token) that must surface to the user, not a stale-session signal.
            const isAuthFlowEndpoint = endpoint.startsWith(AUTH_FLOW_PREFIX);
            if (response.status === 401 && token && !isAuthFlowEndpoint && !retriedAfterRefresh) {
                // Check if we have a refresh token
                const refreshToken = tokenStorage.getRefreshToken();
                if (!refreshToken) {
                    tokenStorage.clearAll();
                    logAuthStage('session-expired-no-refresh-token', { endpoint });
                    // Force redirect to login when no refresh token
                    this.redirectToLogin();
                    throw new ApiError('No refresh token available', 401, {});
                }

                // If already refreshing, queue this request
                if (this.isRefreshing) {
                    return new Promise((resolve, reject) => {
                        this.failedQueue.push({ resolve, reject });
                    }).then(() => {
                        // Retry once with the new token. A second 401 is an
                        // authorization failure, not another expired session.
                        return this.request<T>(endpoint, options, true);
                    });
                }

                this.isRefreshing = true;

                try {
                    await this.refreshTokens();
                    await this.processQueue(null, tokenStorage.getToken());

                    return this.request<T>(endpoint, options, true);
                } catch (refreshError) {
                    await this.processQueue(refreshError as Error, null);
                    tokenStorage.clearAll();
                    // Force redirect to login on refresh failure (preserve return URL)
                    this.redirectToLogin();
                    throw new ApiError('Token refresh failed', 401, {});
                } finally {
                    this.isRefreshing = false;
                }
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));

                // Backend validation errors return `message` as a string array.
                // Joining it gives readable multi-line feedback instead of the
                // comma-mashed string Error() would otherwise produce.
                const rawMessage = (errorData as any)?.message;
                const normalizedMessage = Array.isArray(rawMessage)
                    ? rawMessage.join('\n')
                    : rawMessage;

                if (response.status === 503) {
                    const code = (errorData as any)?.code;
                    if (code === 'MAINTENANCE') {
                        import('@/components/MaintenanceOverlay').then(({ dispatchMaintenanceMode }) => {
                            dispatchMaintenanceMode({
                                code,
                                message: typeof normalizedMessage === 'string' ? normalizedMessage : undefined,
                            });
                        });
                    }
                }

                // Breadcrumb so a failed request's stage is traceable.
                if (endpoint.startsWith(AUTH_FLOW_PREFIX) || endpoint.includes('password')) {
                    logAuthStage('api-error', {
                        endpoint,
                        status: response.status,
                        message: normalizedMessage,
                    });
                }

                const requestId =
                    response.headers.get('x-request-id') ||
                    response.headers.get('x-correlation-id') ||
                    undefined;
                const mintedErrorId =
                    (typeof (errorData as { errorId?: unknown })?.errorId === 'string'
                        ? (errorData as { errorId: string }).errorId
                        : undefined) ||
                    response.headers.get('x-lark-error-id') ||
                    undefined;
                const path = endpoint.split(/[?#]/)[0] || endpoint;
                const apiError = new ApiError(
                    normalizedMessage ||
                        (response.status === 403
                            ? 'Access denied - insufficient permissions'
                            : `HTTP error! status: ${response.status}`),
                    response.status,
                    errorData,
                    requestId,
                    path,
                    mintedErrorId,
                );

                if (!path.includes('/client-errors')) {
                    void import('@/lib/lark-error-capture')
                        .then(({ beginSystemCapture }) =>
                            beginSystemCapture({
                                kind: 'api',
                                error: apiError,
                                requestId,
                                endpoint: path,
                                errorId: mintedErrorId,
                            }),
                        )
                        .catch(() => undefined);
                }

                throw apiError;
            }

            return response.json();
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError('Network error', 0, { originalError: error });
        }
    }

    async get<T>(endpoint: string, options?: RequestInit & { params?: Record<string, any> }): Promise<T> {
        let url = endpoint;

        // Convert params object to query string. Empty strings and empty
        // arrays are omitted — Nest ValidationPipe treats "" as present and
        // 400s optional UUID/enum filters (C-Back-End #137).
        if (options?.params) {
            const queryString = new URLSearchParams(
                compactQueryParams(options.params)
            ).toString();

            if (queryString) {
                url = `${endpoint}?${queryString}`;
            }

            // Remove params from options before passing to request
            const { params, ...restOptions } = options;
            return this.request<T>(url, { ...restOptions, method: 'GET' });
        }

        return this.request<T>(url, { ...options, method: 'GET' });
    }

    async post<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
        // Handle FormData differently - don't stringify it
        const body = data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined);

        return this.request<T>(endpoint, {
            ...options,
            method: 'POST',
            body,
        });
    }

    async put<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
        return this.request<T>(endpoint, {
            ...options,
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async patch<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
        return this.request<T>(endpoint, {
            ...options,
            method: 'PATCH',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
        return this.request<T>(endpoint, { ...options, method: 'DELETE' });
    }
}

// API Error Class
export class ApiError extends Error {
    constructor(
        message: string,
        public status: number,
        public data?: any,
        public requestId?: string,
        public endpoint?: string,
        public errorId?: string,
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

/**
 * Prefer the API's own message so a failed GET/POST is not relabeled as an
 * empty list ("no clients", "0 projects") or a silent create.
 */
export function describeApiError(error: unknown, fallback: string): string {
    if (error instanceof ApiError) {
        const msg = error.message?.trim();
        if (msg) return msg;
    }
    if (error instanceof Error) {
        const msg = error.message?.trim();
        if (msg) return msg;
    }
    return fallback;
}

// Default API client instance
export const apiClient = new ApiClient();
